/**
 * localStorage that never throws.
 *
 * Browsers can refuse storage access: iOS Safari with "Block All Cookies" or in
 * Lockdown Mode, private tabs and in-app browsers, or a full quota (a 5 MB limit
 * shared by everything the site has saved). In those cases raw localStorage calls
 * throw, and an uncaught throw during login used to fail silently.
 *
 * Reads/writes here fall back to an in-memory copy, so the user can still sign in
 * and use the current session; they'd only need to sign in again after a reload.
 */
const memory = new Map();

export function safeGet(key) {
  try {
    const value = window.localStorage.getItem(key);
    if (value !== null) return value;
  } catch {
    // storage blocked — fall through to memory
  }
  return memory.has(key) ? memory.get(key) : null;
}

/** Returns true if the value reached real localStorage, false if only memory has it. */
export function safeSet(key, value) {
  memory.set(key, value);
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function safeRemove(key) {
  memory.delete(key);
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function safeClear() {
  memory.clear();
  try {
    window.localStorage.clear();
  } catch {
    // ignore
  }
  try {
    window.sessionStorage.clear();
  } catch {
    // ignore
  }
}
